import React, { useEffect, useRef, useMemo } from 'react';
import { Player } from '@lordicon/react';
import { DietIdEnum, FormikRadioInput as Radio } from '@front-activerse/common-components';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import useSteps from '../StepContext/useSteps';
import { useFormikContext } from 'formik';
import { dietSettings } from '../types';
import { Row, Col } from 'reactstrap';

export const DietType: React.FC = () => {
  const { t, language } = useTranslator();
  const { dietTypes } = useSteps();
  const { values, setFieldValue } = useFormikContext<dietSettings>();

  const playerRefInfo = useRef<Player>(null);
  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();
    return color || '#000000';
  }, []);

  const {
    images: {
      dietTypeClassic,
      dietTypeClassicPt,
      dietTypeVege,
      dietTypeVegan,
      dietTypeGlp1,
      dietTypeNoGluten,
      dietTypeLowIG,
      dietTypeSmart,
      dietTypeBudget,
      dietTypeHighProtein,
      dietTypeHighProtein2,
      dietTypeHighProteinCz,
      dietTypeHighProteinAntiJojo,
      dietTypeHighProteinAntiJojoCz,
      dietTypeHighProteinSe,
      dietTypeHighProteinVege,
      dietTypeComfort,
      dietTypeInstant,
      dietTypeSimple,
      dietTypeSimpleAntiJojo,
    },
    lotties: { infoIcon },
    consts: { APP_BRAND },
  } = useAppContext();

  useEffect(() => {
    const skipDietLevelReset =
      (APP_BRAND === 'hashimoto' && values.dietType === DietIdEnum.DIET_ID_GLP1) ||
      (APP_BRAND === 'dtba' &&
        (values.dietType === DietIdEnum.DIET_ID_CLASSIC ||
          values.dietType === DietIdEnum.DIET_ID_CLASSIC_LVL2 ||
          values.dietType === DietIdEnum.DIET_ID_CLASSIC_LVL3)) ||
      (APP_BRAND === 'oxy' &&
        (values.dietType === DietIdEnum.DIET_ID_HIGH_PROTEIN ||
          values.dietType === DietIdEnum.DIET_ID_ANTY_JOJO ||
          values.dietType === DietIdEnum.DIET_ID_SIMPLE ||
          values.dietType === DietIdEnum.DIET_ID_SIMPLE_ANTY_JOJO));

    if (!skipDietLevelReset) {
      setFieldValue('dietLevel', null);
    }

    if (
      values.dietType === DietIdEnum.DIET_ID_INSTANT ||
      values.dietType === DietIdEnum.DIET_ID_SIMPLE ||
      values.dietType === DietIdEnum.DIET_ID_SIMPLE_ANTY_JOJO ||
      values.dietType === DietIdEnum.DIET_ID_HIGH_PROTEIN2
    ) {
      setFieldValue('dietVariant', null);
    }

    // only for cs-CZ oxy
    if (
      (values.dietType === DietIdEnum.DIET_ID_HIGH_PROTEIN ||
        values.dietType === DietIdEnum.DIET_ID_ANTY_JOJO) &&
      language === 'cs-CZ'
    ) {
      setFieldValue('dietVariant', null);
    }
  }, [values.dietType, setFieldValue, APP_BRAND, language]);

  const enabledDietTypes = dietTypes?.filter((dietType) =>
    dietType.disableCondition ? !dietType.disableCondition(values) : true
  );
  const disabledDietTypes = dietTypes?.filter((dietType) =>
    dietType.disableCondition ? dietType.disableCondition(values) : false
  );

  const renderImage = (dietType: DietIdEnum) => {
    switch (dietType) {
      case DietIdEnum.DIET_ID_CLASSIC: {
        const isPortuguese = language.startsWith('pt');
        return (
          <img
            src={isPortuguese ? dietTypeClassicPt : dietTypeClassic}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeClassic"
          />
        );
      }
      case DietIdEnum.DIET_ID_VEGE:
        return (
          <img src={dietTypeVege} className="img-fluid mb-s border-radius-m" alt="dietTypeVege" />
        );
      case DietIdEnum.DIET_ID_LOW_IG:
        return (
          <img src={dietTypeLowIG} className="img-fluid mb-s border-radius-m" alt="dietTypeLowIG" />
        );
      case DietIdEnum.DIET_ID_SMART:
        return (
          <img src={dietTypeSmart} className="img-fluid mb-s border-radius-m" alt="dietTypeSmart" />
        );
      case DietIdEnum.DIET_ID_BUDGET:
        return (
          <img
            src={dietTypeBudget}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeBudget"
          />
        );
      case DietIdEnum.DIET_ID_HIGH_PROTEIN: {
        const dietImg = (language: string) => {
          switch (language) {
            case 'sv-SE':
              return dietTypeHighProteinSe;
            case 'cs-CZ':
              return dietTypeHighProteinCz;
            default:
              return dietTypeHighProtein;
          }
        };
        return (
          <img
            src={dietImg(language)}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeHighProtein"
          />
        );
      }
      case DietIdEnum.DIET_ID_HIGH_PROTEIN2:
        return (
          <img
            src={dietTypeHighProtein2}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeHighProtein2"
          />
        );
      case DietIdEnum.DIET_ID_ANTY_JOJO: {
        const dietImg = (language: string) => {
          switch (language) {
            case 'cs-CZ':
              return dietTypeHighProteinAntiJojoCz;
            default:
              return dietTypeHighProteinAntiJojo;
          }
        };
        return (
          <img
            src={dietImg(language)}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeHighProteinAntiJojo"
          />
        );
      }
      case DietIdEnum.DIET_ID_HIGH_PROTEIN_VEGE:
        return (
          <img
            src={dietTypeHighProteinVege}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeHighProteinVege"
          />
        );
      case DietIdEnum.DIET_ID_GLP1:
        return (
          <img src={dietTypeGlp1} className="img-fluid mb-s border-radius-m" alt="dietTypeGlp1" />
        );
      case DietIdEnum.DIET_ID_GLUTEN_DAIRY_FREE:
        return (
          <img
            src={dietTypeNoGluten}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeNoGluten"
          />
        );
      case DietIdEnum.DIET_ID_VEGAN:
        return (
          <img src={dietTypeVegan} className="img-fluid mb-s border-radius-m" alt="dietTypeVegan" />
        );
      case DietIdEnum.DIET_ID_COMFORT:
        return (
          <img
            src={dietTypeComfort}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeComfort"
          />
        );
      case DietIdEnum.DIET_ID_INSTANT:
        return (
          <img
            src={dietTypeInstant}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeInstant"
          />
        );
      case DietIdEnum.DIET_ID_SIMPLE:
        return (
          <img
            src={dietTypeSimple}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeSimple"
          />
        );
      case DietIdEnum.DIET_ID_SIMPLE_ANTY_JOJO:
        return (
          <img
            src={dietTypeSimpleAntiJojo}
            className="img-fluid mb-s border-radius-m"
            alt="dietTypeSimpleAntiJojo"
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <h2 className="mb-4 title-large semi-bold">{t('app.nutritionSettings.dietType.headline')}</h2>

      {disabledDietTypes && disabledDietTypes.length > 0 && (
        <div
          className="available-diet-info-notification d-flex align-items-start"
          onMouseEnter={() => playerRefInfo.current?.playFromBeginning()}
          onTouchStart={() => playerRefInfo.current?.playFromBeginning()}
        >
          <Player ref={playerRefInfo} icon={infoIcon} size={24} colorize={iconsIconMiddleColor} />
          <p className="ms-1">{t('nutritionSettings.diet.notAvailable.notification')}</p>
        </div>
      )}

      {enabledDietTypes.map((dietType) => {
        if (!dietType.renderCondition || dietType.renderCondition(values)) {
          return (
            <Radio name="dietType" value={dietType.value} key={dietType.id}>
              {renderImage(dietType.value)}
              <strong>{t(dietType.optionTitle)}</strong>
              <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                {t(dietType.optionDescription)}
              </p>

              <Row className="mt-m">
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet protein"></div>
                    <div>
                      <p className="description-small mb-0">
                        {t('settings.dietType.macros.proteins')}
                      </p>
                      <p className="small mb-0">{dietType?.macro?.protein}%</p>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet fat"></div>
                    <div>
                      <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                      <p className="small mb-0">{dietType?.macro?.fat}%</p>
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex align-items-start">
                    <div className="bullet carbs"></div>
                    <div>
                      <p className="description-small mb-0">
                        {t('settings.dietType.macros.carbs')}
                      </p>
                      <p className="small mb-0">{dietType?.macro?.carbs}%</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Radio>
          );
        }

        return null;
      })}

      {disabledDietTypes && disabledDietTypes.length > 0 && (
        <>
          <hr className="my-l" />
          <p className="text-start black small">{t('nutritionSettings.notAvailable.headline')}</p>

          {disabledDietTypes.map((dietType) => {
            if (dietType.renderCondition && !dietType.renderCondition(values)) {
              return null;
            }
            return (
              <Radio name="dietType" value={dietType.value} key={dietType.id} disabled>
                {renderImage(dietType.value)}
                <strong>{t(dietType.optionTitle)}</strong>
                <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                  {t(dietType.optionDescription)}
                </p>

                <Row className="mt-m">
                  <Col>
                    <div className="d-flex align-items-start">
                      <div className="bullet protein"></div>
                      <div>
                        <p className="description-small mb-0">
                          {t('settings.dietType.macros.proteins')}
                        </p>
                        <p className="small mb-0">{dietType?.macro?.protein}%</p>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="d-flex align-items-start">
                      <div className="bullet fat"></div>
                      <div>
                        <p className="description-small mb-0">
                          {t('settings.dietType.macros.fats')}
                        </p>
                        <p className="small mb-0">{dietType?.macro?.fat}%</p>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className="d-flex align-items-start">
                      <div className="bullet carbs"></div>
                      <div>
                        <p className="description-small mb-0">
                          {t('settings.dietType.macros.carbs')}
                        </p>
                        <p className="small mb-0">{dietType?.macro?.carbs}%</p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Radio>
            );
          })}
        </>
      )}

      <p className="description text-start mt-xxl mb-xxl text-text-variant-2-color">
        {t('nutritionSettings.dietType.macrosDescription')}
      </p>
    </>
  );
};

export default DietType;
